import {
  Button,
  Checkbox as MUICheckbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  FormControlLabel,
  IconButton
} from '@mui/material';
import classNames from 'classnames';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { DownloadIcon, Icon, Loader, LoaderType } from '@portal/ui-library';
import { EVENT_IDS } from 'pages/DigitalOrderPage/constants';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const DownloadOfferDialog = ({
  isOpen,
  hasConsent,
  onClose,
  onDownload,
  onConsentClick,
  onDownloadDisclaimerDocument
}: {
  isOpen: boolean;
  hasConsent: boolean;
  onClose: () => void;
  onConsentClick: (val: boolean) => void;
  onDownload: () => Promise<void>;
  onDownloadDisclaimerDocument: () => Promise<void>;
}) => {
  const { t } = useTranslation('wizardSidebar');
  const [isDownloading, setIsDownloading] = useState(false);

  async function handleDownloadDisclaimer(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();

    setIsDownloading(true);
    try {
      await onDownloadDisclaimerDocument();
    } finally {
      setIsDownloading(false);
    }
  }

  async function handleDownloadOffer() {
    setIsDownloading(true);
    try {
      await onDownload();
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Dialog fullWidth maxWidth={'md'} open={isOpen} onClose={onClose} disableScrollLock>
      <DialogTitle className="flex items-center justify-between">
        <Typography variant="bodyMDBold" className="text-text-80">
          {t('downloadOffer.title')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('downloadOffer.close')}>
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="text-center s:text-left  space-y-6">
        <div
          className={classNames('p-m rounded-md  text-left', {
            'bg-primary-40': hasConsent,
            'bg-surface-60': !hasConsent
          })}
        >
          <FormControl>
            <FormControlLabel
              id={EVENT_IDS.DATA_PROTECTION_CHECKBOX}
              control={
                <MUICheckbox
                  checked={hasConsent}
                  onChange={(_, checked) => onConsentClick(checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="bodyMDRegular" className="text-text-80 pl-[8px]">
                  <Trans
                    i18nKey={'wizardSidebar:downloadOffer.disclaimerCheckboxText'}
                    components={[
                      <a
                        key="downloadDisclaimer"
                        onClick={handleDownloadDisclaimer}
                        className="text-navigation-80"
                      />
                    ]}
                  />
                </Typography>
              }
            />
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions className={'pt-l flex justify-end'}>
        <Button variant="text" className="mr-s s:mb-s" onClick={onClose}>
          {t('downloadOffer.cancel')}
        </Button>
        <Button
          variant="contained"
          id={EVENT_IDS.DOWNLOAD_OFFER}
          onClick={handleDownloadOffer}
          disabled={!hasConsent}
          startIcon={
            isDownloading ? (
              <Loader type={LoaderType.Circular} />
            ) : (
              <Icon size="xs" icon={<DownloadIcon />} />
            )
          }
        >
          {t('downloadOffer.download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
